import { useCallback, useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Upload, X, Image as ImageIcon, Scan } from "lucide-react";
import { Button } from "@/components/ui/button";

export const UploadZone = ({ 
  label, 
  preview, 
  onUpload, 
  onClear, 
  isAnalyzing,
  ...props 
}) => {
  const [isDragActive, setIsDragActive] = useState(false);
  const fileInputRef = useRef(null);

  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setIsDragActive(true);
    } else if (e.type === "dragleave") {
      setIsDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      onUpload(e.dataTransfer.files[0]);
    }
  }, [onUpload]);

  const handleFileInput = useCallback((e) => {
    if (e.target.files && e.target.files[0]) {
      onUpload(e.target.files[0]);
    }
  }, [onUpload]);

  const handleClick = () => {
    fileInputRef.current?.click();
  };

  return (
    <div {...props}>
      <div className="text-xs font-mono text-zinc-500 uppercase tracking-widest mb-3 flex items-center gap-2">
        <ImageIcon className="w-4 h-4" />
        {label}
      </div>
      
      <div
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
        onClick={!preview ? handleClick : undefined}
        className={`
          relative min-h-[350px] border-2 border-dashed rounded-sm
          flex flex-col items-center justify-center
          transition-all cursor-pointer overflow-hidden
          ${isDragActive 
            ? "border-cyan-500 bg-cyan-500/5" 
            : preview 
              ? "border-zinc-700 bg-zinc-900/30" 
              : "border-zinc-700 hover:border-cyan-500/50 bg-zinc-900/20"
          }
        `}
        data-testid={`${props['data-testid']}-dropzone`}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          onChange={handleFileInput}
          className="hidden"
          data-testid={`${props['data-testid']}-input`}
        />

        <AnimatePresence mode="wait">
          {preview ? (
            <motion.div
              key="preview"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="relative w-full h-full min-h-[350px]"
            >
              <img
                src={preview}
                alt={`${label} preview`}
                className="w-full h-full object-contain bg-zinc-900"
                data-testid={`${props['data-testid']}-preview`}
              />
              
              {/* Scanning animation overlay */}
              {isAnalyzing && (
                <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                  <div className="absolute inset-0 overflow-hidden">
                    <div className="scan-line animate-scan" />
                  </div>
                  <div className="flex items-center gap-2 text-cyan-400 font-mono text-sm uppercase tracking-wider z-10">
                    <Scan className="w-5 h-5 animate-pulse" />
                    Scanning...
                  </div>
                </div>
              )}
              
              {/* Clear button */}
              {!isAnalyzing && (
                <Button
                  onClick={(e) => {
                    e.stopPropagation();
                    onClear();
                  }}
                  variant="ghost"
                  size="icon"
                  className="absolute top-3 right-3 bg-black/50 hover:bg-black/70 text-white rounded-sm w-8 h-8"
                  data-testid={`${props['data-testid']}-clear`}
                >
                  <X className="w-4 h-4" />
                </Button>
              )}
            </motion.div>
          ) : (
            <motion.div
              key="upload"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="flex flex-col items-center justify-center p-8 text-center"
            >
              <div className={`
                p-4 rounded-full mb-4 transition-all
                ${isDragActive 
                  ? "bg-cyan-500/20 text-cyan-400" 
                  : "bg-zinc-800 text-zinc-400"
                }
              `}>
                <Upload className="w-8 h-8" />
              </div>
              
              <div className="font-mono text-zinc-300 mb-2">
                {isDragActive ? "Drop image here" : "Drag & drop image"}
              </div>
              
              <div className="text-zinc-500 text-sm mb-4">
                or click to browse
              </div>
              
              <div className="text-zinc-600 text-xs uppercase tracking-wider">
                JPEG, PNG, WEBP • Max 10MB
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default UploadZone;
